/**
 * The snapshot editor's long jobs, held above the dialogs that start them.
 *
 * A build takes minutes and a delivery uploads to three services; both run in
 * the agent container and finish whether or not anyone is watching. What used
 * to watch them was the dialog itself — the polling loop lived inside it and
 * its result went into its own state — so the dialog had to stay open and
 * modal for the whole run, and a person who had pressed Build could do nothing
 * else until it came back.
 *
 * So the job lives here instead, for as long as the editor is open:
 *
 *   - closing the dialog leaves the job running, and reopening it re-attaches
 *     to the same job rather than starting a second one;
 *   - a second folder can be built while the first is still going — the
 *     container locks per task, not globally, so two folders genuinely build
 *     in parallel;
 *   - a reload picks the jobs back up from the server (`listJobs`), because a
 *     job is the server's record, not the page's;
 *   - whatever must happen when a job ends — the snackbar, the snapshot
 *     refresh, the note that a delivery's outcome was never seen — happens
 *     here, so it happens with the dialog closed too.
 *
 * The dialogs keep the parts that are theirs: what to show, and what to do with
 * the result once they are looking at it (`consume`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import {
  JOB_CONNECTION_LOST,
  JOB_LOST,
  describeJobFailureWithReasons,
  followJob,
  jobFailureCode,
  listSnapshotJobs,
} from "../modules/snapshotEditorApi";

/** A job is identified by what it does and what it does it to. */
function jobKey(type, scope = "") {
  return `${type}:${scope || ""}`;
}

/** The folder's own name — what a message about a background job has to name. */
function scopeName(scope) {
  return String(scope || "").split("/").pop() || "";
}

/** How many of these events a job has reported — what progress is counted in. */
function countEvents(events, ...types) {
  return (events || []).filter((event) => types.includes(event.type)).length;
}

/** The delivery steps as the task dialog names them (its STEP_LABELS). */
const DELIVERY_STEP_LABELS = {
  plec: "uploading to the AppLovin preview tool",
  drive: "copying to Google Drive",
  // A comment for Audience+, a hand-off on the subtask for Ads Manager.
  asana: "updating Asana",
};

/**
 * What each kind of job is, in the words the person who started it needs.
 *
 * `repeatable` is not decoration: a build overwrites its own artifacts, so
 * "try again" is the right advice; a delivery posts a SECOND Asana comment, so
 * the same words would be a trap. See describeJobFailure.
 */
const JOB_KINDS = {
  previewTask: {
    noun: "Build",
    readIn: "the task",
    repeatable: true,
    // Works on one task's files, so the tree must not move them underneath it.
    locksScope: true,
    // The build stamps the task id onto the snapshots, so the tree's copies are
    // out of date the moment it finishes.
    refreshesSnapshots: true,
    progress: (events, stage) => {
      const done = countEvents(events, "built", "build_failed");
      if (stage.stage === "build") {
        return `Building in the agent container — ${done} of ${stage.total} done.`;
      }
      if (stage.stage === "upload") {
        return `Uploading the builds — ${countEvents(events, "uploaded")} of ${stage.total} done.`;
      }
      return null;
    },
    started: (scope) => [`Building "${scopeName(scope)}" — you can close this and keep working.`, "info"],
    finished: (data, scope) => {
      const failures = (_.get(data, "failed") || []).length;
      return failures
        ? [`Build finished for "${scopeName(scope)}" — ${failures} of them failed.`, "warning"]
        : [`Build finished for "${scopeName(scope)}".`, "success"];
    },
  },

  deliverTask: {
    noun: "Submit",
    readIn: "the task",
    repeatable: false,
    locksScope: true,
    // A step reports twice — started, then done or failed — so count only the
    // ones that finished, and name the one under way: the preview tool upload
    // carries every SIP in one request and can take minutes on its own.
    progress: (events, stage) => {
      if (stage.stage !== "deliver") return null;
      const steps = (events || []).filter((event) => event.type === "step");
      const done = steps.filter((event) => event.state === "done").length;
      const last = _.last(steps);
      const current = last && last.state === "started" ? DELIVERY_STEP_LABELS[last.step] || last.step : null;
      return `Sending${current ? ` — ${current}` : ""} (${done} of ${stage.total} steps done).`;
    },
    started: (scope, meta) =>
      meta.dryRun
        ? [`Composing the test delivery for "${scopeName(scope)}" — nothing will be sent.`, "info"]
        : [`Submitting "${scopeName(scope)}" — you can close this and keep working.`, "info"],
    finished: (data, scope, meta) => {
      if (_.get(data, "dryRun") || meta.dryRun) {
        return [`Test finished for "${scopeName(scope)}" — nothing was sent.`, "info"];
      }
      if (!_.get(data, "ok")) {
        return [`Delivery of "${scopeName(scope)}" stopped — open the task to see which step.`, "warning"];
      }
      return [meta.wasDelivered ? `Re-delivered "${scopeName(scope)}".` : `Delivered "${scopeName(scope)}".`, "success"];
    },
  },

  exportSnapshots: {
    noun: "Export",
    readIn: "Export",
    repeatable: true,
    // The build measures what it produced (controller/snapshotEditor/buildSizes.js),
    // so the tree is a listing out of date the moment the export lands: until it
    // is re-read the size on screen is still the pre-build estimate, which is an
    // upper bound and reads as the export having come out wrong.
    refreshesSnapshots: true,
    progress: (events, stage) =>
      stage.stage === "package"
        ? "Packaging the artifacts…"
        : `Building in the agent container — ${countEvents(events, "built", "build_failed")} of ${
          stage.total
        } done.`,
    started: () => ["Exporting — you can close this and keep working.", "info"],
    finished: (data) => {
      const failures = (_.get(data, "failed") || []).length;
      return failures
        ? [`Export ready — ${failures} build(s) failed.`, "warning"]
        : ["Export ready — open the Export dialog for the file.", "success"];
    },
  },
};

/**
 * Where an unanswered delivery survives a reload.
 *
 * Per base and per browser, which is exactly the scope of the question: it is
 * THIS page that stopped hearing from the container, and whoever reloads it is
 * the person who still has to check Asana and Drive before sending again. The
 * server cannot hold it — the case that raises it is the server having lost the
 * job — so it lives in localStorage, where a refresh cannot take it away.
 */
function unresolvedStorageKey(baseID) {
  return baseID ? `snapshotEditor.unresolvedDeliveries.${baseID}` : null;
}

function readUnresolved(baseID) {
  const key = unresolvedStorageKey(baseID);
  if (!key) return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "null");
    return _.isPlainObject(parsed) ? parsed : {};
  } catch (_err) {
    // A browser with storage switched off still gets the question for as long
    // as the page lives, which is what it got before this existed.
    return {};
  }
}

function writeUnresolved(baseID, value) {
  const key = unresolvedStorageKey(baseID);
  if (!key) return;
  try {
    if (_.isEmpty(value)) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_err) {
    // See readUnresolved.
  }
}

/**
 * @param {string|null} baseID              the editor's base; null off an editor base
 * @param {function} createSnackbar         the dashboard's own snackbar
 * @param {function} refreshSnapshots       re-read the tree after a job changed it
 */
export default function useSnapshotJobs({ baseID, createSnackbar, refreshSnapshots }) {
  // key → { key, type, scope, meta, state, startedAt, events, connectionDown, response }
  const [jobs, setJobs] = useState({});

  // Claimed synchronously, unlike the state above: two clicks in one tick must
  // not both get past the "is this already running" check.
  const runningRef = useRef(new Set());

  // Deliveries whose outcome nobody saw — the connection went, or the server
  // restarted — keyed by folder. The container does not stop when the dashboard
  // stops listening, so those steps have most likely completed, and the next
  // Submit has to ask before posting a second Asana comment. Survives closing
  // the dialog AND reloading the page, which is the point: neither is an answer.
  const [unresolvedDeliveries, setUnresolvedDeliveries] = useState({});

  // Read inside the polling loops, which outlive the render that started them.
  const snackbarRef = useRef(createSnackbar);
  const refreshRef = useRef(refreshSnapshots);
  const baseRef = useRef(baseID);
  const unresolvedRef = useRef({});
  snackbarRef.current = createSnackbar;
  refreshRef.current = refreshSnapshots;
  baseRef.current = baseID;

  /** Change the unanswered set and put it where a reload will find it. */
  const rememberUnresolved = useCallback((next) => {
    unresolvedRef.current = next;
    writeUnresolved(baseRef.current, next);
    setUnresolvedDeliveries(next);
  }, []);

  // This base's unanswered questions, and never the previous base's.
  useEffect(() => {
    const stored = readUnresolved(baseID);
    unresolvedRef.current = stored;
    setUnresolvedDeliveries(stored);
  }, [baseID]);

  // A job can end after the editor is gone — a different base was picked while
  // it ran. The work and its snackbar still stand; only this map has nowhere
  // left to go.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const patchJob = useCallback((key, patch) => {
    if (!mountedRef.current) return;
    setJobs((current) => ({ ...current, [key]: { ...(current[key] || {}), ...patch } }));
  }, []);

  /**
   * Everything a finished job owes the person who started it, whether or not
   * they are still looking at the dialog.
   */
  const settle = useCallback(
    async (job, response, { silent = false } = {}) => {
      const { key, meta } = job;
      const kind = JOB_KINDS[job.type] || {};

      runningRef.current.delete(key);

      if (!response.ok) {
        // `outcome` travels with the result so the tree's mark is never briefly
        // the wrong colour.
        patchJob(key, {
          state: "finished",
          finishedAt: Date.now(),
          connectionDown: null,
          response,
          outcome: "error",
        });

        const code = jobFailureCode(response);
        // Nobody saw the end of this one, and the steps run on regardless.
        if (
          job.type === "deliverTask" &&
          !meta.dryRun &&
          mountedRef.current &&
          (code === JOB_CONNECTION_LOST || code === JOB_LOST)
        ) {
          rememberUnresolved({ ...unresolvedRef.current, [job.scope]: { at: Date.now() } });
        }
        if (!silent) {
          snackbarRef.current?.(
            `${kind.noun || "Job"} failed: ${describeJobFailureWithReasons(response, {
              fallback: `${kind.noun || "Job"} failed.`,
              repeatable: kind.repeatable !== false,
            })}`,
            "error",
            false
          );
        }
        return;
      }

      // It answered, so whatever the outcome the question is no longer open.
      if (job.type === "deliverTask" && !meta.dryRun && mountedRef.current) {
        rememberUnresolved(_.omit(unresolvedRef.current, [job.scope]));
      }

      const [message, variant] = kind.finished
        ? kind.finished(response.data, job.scope, meta)
        : [`${kind.noun || "Job"} finished.`, "success"];
      patchJob(key, {
        state: "finished",
        finishedAt: job.finishedAt || Date.now(),
        connectionDown: null,
        response,
        outcome: variant,
      });
      // A job that had already ended before this page existed is picked up for
      // its RESULT, not for its news: announcing it would tell whoever reloaded
      // that a delivery just went out, on every reload, for half an hour.
      if (!silent) snackbarRef.current?.(message, variant, false);

      // Not for a job that had already ended: the tree this page loaded was read
      // after it finished, so it already carries whatever the job stamped.
      if (kind.refreshesSnapshots && !silent && mountedRef.current && refreshRef.current) {
        await refreshRef.current();
      }
    },
    [patchJob, rememberUnresolved]
  );

  /** Follow a job to the end, reporting as it goes. Used to start and to re-attach. */
  const watch = useCallback(
    (job, run, options = {}) => {
      const hooks = {
        onProgress: (events) => patchJob(job.key, { events }),
        onConnectionState: (state) =>
          patchJob(job.key, { connectionDown: state.down ? state : null }),
        // Carried to the server with the request that starts the job, so a
        // reload — which finds the job but not the page that started it — gets
        // the meta back from `listJobs` instead of reporting without it.
        jobMeta: job.meta,
      };
      return Promise.resolve(run(hooks))
        .then((response) => settle(job, response, options))
        .catch((err) =>
          settle(job, { ok: false, status: 0, error: String(err.message || err) }, options)
        );
    },
    [patchJob, settle]
  );

  /**
   * Start a job, unless one is already running for the same thing.
   *
   * `run` is given the progress hooks and returns the API call — which one it
   * is stays with the dialog, since only the dialog knows what it is asking for.
   *
   * @param {string} type    a key of JOB_KINDS
   * @param {string} scope   the folder (or file) it works on; "" for the editor
   * @param {object} [meta]  what the messages need but the response does not
   *   carry — a dry run, a task that had been delivered before
   */
  const start = useCallback(
    ({ type, scope = "", meta = {}, run }) => {
      const key = jobKey(type, scope);
      if (runningRef.current.has(key)) return null;

      runningRef.current.add(key);
      patchJob(key, {
        key,
        type,
        scope,
        meta,
        state: "running",
        startedAt: Date.now(),
        events: [],
        connectionDown: null,
        response: null,
      });

      const kind = JOB_KINDS[type] || {};
      if (kind.started) {
        const [message, variant] = kind.started(scope, meta);
        snackbarRef.current?.(message, variant, false);
      }

      return watch({ key, type, scope, meta }, run);
    },
    [patchJob, watch]
  );

  /**
   * Take a finished job's result off the board.
   *
   * Called by the dialog once it has shown the result — which may be minutes
   * after the job ended, since a result waits for the dialog to be reopened
   * rather than the other way round.
   */
  const consume = useCallback((key) => {
    if (!mountedRef.current) return;
    setJobs((current) => _.omit(current, [key]));
  }, []);

  const get = useCallback((type, scope = "") => jobs[jobKey(type, scope)] || null, [jobs]);

  /**
   * One job's own line of progress, in the words its kind uses.
   *
   * Here rather than in the dialogs because the drawer shows the same jobs they
   * do — a build reads the same whether it is being watched in the task dialog
   * or in the list of everything running.
   */
  const progressOf = useCallback((job) => {
    const describe = _.get(JOB_KINDS, [_.get(job, "type"), "progress"]);
    const stage = _.findLast(_.get(job, "events") || [], (event) => event.type === "stage");
    return describe && stage ? describe(job.events, stage) : null;
  }, []);

  /**
   * Every job this editor knows about — what the drawer lists.
   *
   * Running first, because those are the ones still worth watching; a finished
   * one stays until the dialog that owns it has read its result (`consume`), so
   * this is also where an outcome nobody was in front of ends up.
   */
  const list = useMemo(
    () =>
      _.orderBy(
        Object.values(jobs).map((job) => ({
          ...job,
          noun: _.get(JOB_KINDS, [job.type, "noun"], "Job"),
          // A build or a delivery is named by the folder it works on; an export
          // has no folder of its own and names its target in `meta`.
          target:
            _.get(JOB_KINDS, [job.type, "locksScope"]) === true
              ? job.scope
              : _.get(job, "meta.target", ""),
          // Only a task can be reopened — an export's result lives in its own
          // dialog, which has no folder to go back to.
          folder: _.get(JOB_KINDS, [job.type, "locksScope"]) === true ? job.scope : null,
        })),
        [(job) => (job.state === "running" ? 0 : 1), (job) => job.finishedAt || job.startedAt],
        ["asc", "desc"]
      ),
    [jobs]
  );

  /**
   * Whether a job that reads this folder's files is running on it.
   *
   * Per folder rather than globally: that is what the container enforces (one
   * task at a time, any number of tasks side by side), and what lets someone
   * build one folder while another is being delivered. What it is for is the
   * tree — renaming a folder, or moving a file out of it, while it is being
   * built ships something nobody approved. An export is not a task and locks
   * nothing, which is why the kinds say so rather than this counting them all.
   */
  const isScopeBusy = useCallback(
    (scope = "") =>
      Object.values(jobs).some(
        (job) =>
          job.state === "running" &&
          job.scope === (scope || "") &&
          _.get(JOB_KINDS, [job.type, "locksScope"]) === true
      ),
    [jobs]
  );

  /**
   * What the tree shows against one row — a folder path, or a file name — or
   * null. The snackbar is gone in seconds; this stays until the result is read
   * and `consume` clears the job. Running beats finished.
   *
   * A build or a delivery matches the folder it works on. An export has no
   * folder of its own, so it names its target in `meta` and can land on either
   * kind of row: the preview pane it otherwise reports in can be collapsed out
   * of sight.
   */
  const rowStatus = useCallback(
    (target) => {
      if (!target) return null;

      const mine = Object.values(jobs).filter((job) =>
        _.get(JOB_KINDS, [job.type, "locksScope"]) === true
          ? job.scope === target
          : _.get(job, "meta.target") === target
      );
      if (mine.length === 0) return null;

      const describe = (job, state) => ({
        state,
        outcome: state === "finished" ? job.outcome || "success" : undefined,
        noun: _.get(JOB_KINDS, [job.type, "noun"]),
        readIn: _.get(JOB_KINDS, [job.type, "readIn"], "the task"),
      });

      const running = mine.find((job) => job.state === "running");
      if (running) return describe(running, "running");
      return describe(_.maxBy(mine, "finishedAt"), "finished");
    },
    [jobs]
  );

  const dismissUnresolvedDelivery = useCallback(
    (scope) => rememberUnresolved(_.omit(unresolvedRef.current, [scope || ""])),
    [rememberUnresolved]
  );

  // Pick up whatever this user has on this base. A job is the server's record,
  // so a reloaded page — or a second tab — finds it here and follows it to the
  // end instead of leaving it to finish unseen.
  //
  // Finished ones are picked up too (listJobs). Their result is what the dialogs
  // are waiting for — a build that landed during the reload has its files in S3
  // and its links in the job — so it is read back and left on the board for
  // whoever opens that folder. Silently: it is already old news, and announcing
  // it would report the same delivery on every reload.
  useEffect(() => {
    if (!baseID) return undefined;

    let cancelled = false;
    (async () => {
      const response = await listSnapshotJobs(baseID);
      if (cancelled || !response.ok) return;

      for (const job of _.get(response, "data.jobs") || []) {
        if (!JOB_KINDS[job.type]) continue;
        const key = jobKey(job.type, job.scope);
        if (runningRef.current.has(key)) continue;

        const wasFinished = job.state === "finished";
        runningRef.current.add(key);
        // What the page that started it knew and the response does not carry —
        // a dry run, a task that had been delivered before, the tree row an
        // export marks. Sent with the request and handed back by `listJobs`,
        // so a reload reports the job the same way the tab that started it
        // would have.
        const meta = _.isPlainObject(job.meta) ? job.meta : {};

        patchJob(key, {
          key,
          type: job.type,
          scope: job.scope || "",
          meta,
          // Running until its result has been read back, even when the server
          // already calls it finished: the dialogs act on a finished job, and
          // acting on one whose response has not arrived yet is acting on null.
          state: "running",
          startedAt: job.startedAt,
          finishedAt: job.finishedAt || null,
          events: job.progress || [],
          connectionDown: null,
          response: null,
        });

        void watch(
          { key, type: job.type, scope: job.scope || "", meta, finishedAt: job.finishedAt || null },
          (hooks) => followJob({ baseID, jobId: job.id, jobType: job.type }, hooks),
          { silent: wasFinished }
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [baseID, patchJob, watch]);

  return {
    consume,
    dismissUnresolvedDelivery,
    get,
    isScopeBusy,
    list,
    progressOf,
    rowStatus,
    start,
    unresolvedDeliveries,
  };
}
